import { useEffect, useState, useCallback } from 'react';
import { fetchAllOwnedIvrAvatars } from '../api/ivrAgents.js';

/**
 * useAvatarsList
 *   - avatars: 排序後的陣列（啟用優先 + updatedAt desc）
 *   - reload: 強制重 fetch（會 setLoading=true，UI 會閃）
 *   - silentReload: 後台 fetch，不 flip loading（用於背景重整）
 *   - optimisticUpdate(avatarId, patch): 本地 state 直接改 + re-sort，**不打 API**
 *     用於 toggle / rename 等 — 已寫成功才呼叫，避免 UI 閃爍
 */
function sortAvatars(list) {
    return [...list].sort((a, b) => {
        const ea = a.enabled === true ? 1 : 0;
        const eb = b.enabled === true ? 1 : 0;
        if (ea !== eb) return eb - ea;
        const ta = toMillis(a.updatedAt) || toMillis(a.createdAt) || 0;
        const tb = toMillis(b.updatedAt) || toMillis(b.createdAt) || 0;
        return tb - ta;
    });
}

function toMillis(t) {
    if (!t) return 0;
    if (typeof t === 'number') return t;
    if (typeof t === 'string') return new Date(t).getTime() || 0;
    if (t._seconds) return t._seconds * 1000;
    if (t.seconds) return t.seconds * 1000;
    if (t.toMillis) return t.toMillis();
    return 0;
}

export function useAvatarsList(hostId) {
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const reload = useCallback(async ({ refresh = false } = {}) => {
        setLoading(true);
        try {
            const list = await fetchAllOwnedIvrAvatars({ refresh, hostId });
            setAvatars(list);
            setError(null);
        } catch (e) {
            console.error('[useAvatarsList]', e);
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [hostId]);

    /** 背景重 fetch — 不 flip loading，UI 不閃 */
    const silentReload = useCallback(async ({ refresh = false } = {}) => {
        try {
            const list = await fetchAllOwnedIvrAvatars({ refresh, hostId });
            setAvatars(list);
            setError(null);
        } catch (e) {
            console.warn('[useAvatarsList][silent]', e);
        }
    }, [hostId]);

    /** 本地直接改某 avatar 欄位 + re-sort，不打 API。
     *  例如 toggle enabled 成功後呼叫 → 卡片立刻變色 + 滑到正確位置，無閃爍 */
    const optimisticUpdate = useCallback((avatarId, patch) => {
        setAvatars((prev) => {
            const next = prev.map((a) =>
                a.avatarId === avatarId ? { ...a, ...patch } : a
            );
            return sortAvatars(next);
        });
    }, []);

    /** 直接從 local list 拿掉一筆（給刪除用），不打 API、不 reload */
    const optimisticRemove = useCallback((avatarId) => {
        setAvatars((prev) => prev.filter((a) => a.avatarId !== avatarId));
    }, []);

    useEffect(() => { reload(); }, [reload]);

    return { avatars, loading, error, reload, silentReload, optimisticUpdate, optimisticRemove };
}
